<template>
  <article v-if="series && Object.keys(series).length" class="space-0-above">
    <FindingAidsHeader
      :obj="{ breadcrumb: series.collection.callNumber.toLowerCase() }"
    ></FindingAidsHeader>
    <div :class="$style.content">
      <h1 class="no-border-above">{{ series.title }}</h1>
      <nav :class="$style.breadcrumbs">
        Contained in:
        <router-link :to="{ path: '/library-archives/archives-collections' }">
          Finding Aids
        </router-link>
        <IconChevronRight></IconChevronRight>
        <router-link
          :to="{
            name: 'FindingAidsCollection',
            params: { slug: series.collection.callNumber.toLowerCase() },
          }"
          v-html="`${series.collection.title} (${series.collection.callNumber})`"
        ></router-link>
      </nav>
      <ProcessContent :content="series.content" wrapper="section"></ProcessContent>
      <aside>
        <ContentDrawer
          :end-title="'Hide series in this collection'"
          :title="'Show series in this collection'"
        >
          <ul :class="[$style.list, 'body-1']">
            <li v-for="(item, key) in filterSeries(series.collection.series)" :key="key">
              <router-link
                :to="{ name: 'FindingAidsSeries', params: { slug: item.slug } }"
                v-html="item.title"
              ></router-link>
            </li>
          </ul>
        </ContentDrawer>
      </aside>
      <FileList v-if="series.files" :class="$style.spaced" :files="series.files"></FileList>
      <section v-if="series.subseries">
        <div v-for="(item, key) in series.subseries" :key="key" :class="$style.spaced">
          <h2 v-html="`Subseries ${item.label}`"></h2>
          <h3>Folder List</h3>
          <FileList :files="item.files"></FileList>
        </div>
      </section>
    </div>
  </article>
  <BackToTop></BackToTop>
</template>

<script setup lang="ts">
import BackToTop from '@/components/common/BackToTop.vue';
import ContentDrawer from '@/components/shortcodes/ContentDrawer.vue';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import FileList from '@/components/findingAids/FileList.vue';
import { FindingAid } from '@/types/findingaids.interface';
import FindingAidsHeader from '@/components/findingAids/FindingAidsHeader.vue';
import { findingAidsSeriesQuery } from '@/queries/GetFindingAidsSeries';
import IconChevronRight from '@/assets/svgs/IconChevronRight.svg?component';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { ref } from 'vue';
import { useQuery } from 'villus';

const props = defineProps<{
  slug: string;
}>();

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const series = ref<FindingAid>();
const { data, error } = useQuery({
  onData: (data) => {
    if (data.findingAidsSeries && Object.keys(data.findingAidsSeries).length) {
      series.value = data.findingAidsSeries;
      setMetaTitle(data.findingAidsSeries.title);
      setMetaDescription(data.findingAidsSeries.excerpt);
      dataLoaded();
    }

    if (!data.findingAidsSeries || Object.keys(data.findingAidsSeries).length === 0) {
      handleError('FindingAidsSeries', error);
    }
  },
  query: findingAidsSeriesQuery,
  variables: { slug: props.slug },
});

function filterSeries(filterSeries): array {
  return filterSeries.filter((item) => item.title !== series.value.title);
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: findingAidsSeriesQuery,
          variables: { slug: params.slug },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
.breadcrumbs {
  @include body-2;

  & {
    display: inline-block;
    padding: 0.75rem 0;
  }

  svg {
    height: 0.625em;
    padding: 0 0.125rem;
    vertical-align: initial;
    width: 0.625em;
  }
}

.list {
  margin-left: 0;

  li {
    list-style: none;
  }
}

.subseries {
  .fa-folder-list {
    border-top: 1px solid $white;
    padding-top: 1rem;
  }
}

.spaced {
  @include space-48-64-above;
  h2,
  h3,
  h4 {
    color: $black;
  }
}

@media (min-width: $break-point) {
  .content {
    @include grid-content;
  }
}
</style>
